import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const DAYS_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

const TIME_SLOTS = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = i % 2 === 0 ? '00' : '30';
  return `${hour.toString().padStart(2, '0')}:${minute}`;
});

interface AvailabilitySlot {
  day: typeof DAYS_OF_WEEK[number];
  start_time: string;
  end_time: string;
  is_available: boolean;
}

interface WeeklyAvailability {
  id?: string;
  user_id: string;
  slots: AvailabilitySlot[];
}

export const AvailabilityManager: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: availability, isLoading } = useQuery({
    queryKey: ['availability'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('availability')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (!data) {
        // Return default availability if none exists
        return {
          user_id: user?.id,
          slots: DAYS_OF_WEEK.map((day) => ({
            day,
            start_time: '09:00',
            end_time: '17:00',
            is_available: day !== 'Saturday' && day !== 'Sunday',
          })),
        } as WeeklyAvailability;
      }

      return data as WeeklyAvailability;
    },
    enabled: !!user?.id,
  });

  const updateAvailability = useMutation({
    mutationFn: async (data: WeeklyAvailability) => {
      const { error } = await supabase
        .from('availability')
        .upsert({
          id: data.id,
          user_id: data.user_id,
          slots: data.slots,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability'] });
      toast.success('Availability updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update availability');
      console.error('Availability update error:', error);
    },
  });

  const handleSlotUpdate = (
    day: typeof DAYS_OF_WEEK[number],
    field: keyof AvailabilitySlot,
    value: string | boolean
  ) => {
    if (!availability) return;

    const newSlots = availability.slots.map((slot) =>
      slot.day === day ? { ...slot, [field]: value } : slot
    );

    updateAvailability.mutate({
      ...availability,
      slots: newSlots,
    });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Weekly Availability</CardTitle>
        <CardDescription>
          Set your regular working hours for each day of the week
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {DAYS_OF_WEEK.map((day) => {
            const slot = availability?.slots.find((s) => s.day === day);
            if (!slot) return null;

            return (
              <div
                key={day}
                className="flex items-center justify-between space-x-4 py-2"
              >
                <div className="flex items-center space-x-4">
                  <Switch
                    checked={slot.is_available}
                    onCheckedChange={(checked) =>
                      handleSlotUpdate(day, 'is_available', checked)
                    }
                  />
                  <span className="w-24 font-medium">{day}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Select
                    value={slot.start_time}
                    onValueChange={(value) =>
                      handleSlotUpdate(day, 'start_time', value)
                    }
                    disabled={!slot.is_available}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span>to</span>
                  <Select
                    value={slot.end_time}
                    onValueChange={(value) =>
                      handleSlotUpdate(day, 'end_time', value)
                    }
                    disabled={!slot.is_available}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}; 